import { jwtDecode } from 'jwt-decode';
import { featureFlags } from './featureFlags';

/**
 * Build the App-level `user` object from a Cognito session and idToken.
 * Single source of truth for role/group flag derivation so the bootstrap
 * path (App.tsx checkAuth) and the post-login path (AuthCallback) produce
 * identical state — fixes the "dashboard needs refresh after login" race
 * caused by AuthCallback only setting `isAuthenticated` without `user`.
 *
 * Also writes the role flags to localStorage for consumers like Navbar
 * that read them directly.
 */
export function buildUserFromToken(currentUser: any, idToken: string): any {
    const decoded: any = jwtDecode(idToken);

    const groupsRaw = decoded['cognito:groups'] || [];
    const groups = Array.isArray(groupsRaw) ? groupsRaw : [groupsRaw];
    const rawRole = decoded['custom:role'] || decoded.role;
    const roles = Array.isArray(rawRole) ? rawRole : [rawRole || 'USER'];

    // SuperAdmin is gated on group membership or explicit custom:role only.
    // The previous `@compliance-engine.io` email-suffix grant was removed
    // alongside the backend change (OWASP A01) — backend enforcement is the
    // real gate; this UI flag controls which menus render.
    //
    // Group spellings differ per Cognito pool ("SuperAdmins", "Super-Admin",
    // "super_admin"), so compare on a normalised form — the US pools both use
    // "Super-Admin", which the old exact match silently failed to recognise
    // (Super Admin menu never rendered for US staff).
    const normalizeGroup = (g: string) => g.toLowerCase().replace(/[^a-z0-9]/g, '');
    const isSuperAdmin =
        roles.includes('SUPER_ADMIN') ||
        groups.some((g: string) => {
            if (typeof g !== 'string') return false;
            const n = normalizeGroup(g);
            return n === 'superadmin' || n === 'superadmins';
        });

    const isAdmin =
        isSuperAdmin ||
        roles.includes('ADMIN') ||
        groups.some((g: string) => {
            if (typeof g !== 'string') return false;
            const n = normalizeGroup(g);
            return n === 'admin' || n === 'admins';
        });

    localStorage.setItem('isSuperAdmin', isSuperAdmin.toString());
    localStorage.setItem('isAdmin', isAdmin.toString());
    localStorage.setItem(
        'userRole',
        isSuperAdmin ? 'SUPER_ADMIN' : isAdmin ? 'ADMIN' : (roles[0] || 'USER')
    );
    const subStatus = decoded['custom:subscriptionStatus'] || 'active';
    localStorage.setItem('subscriptionStatus', subStatus);

    return {
        ...currentUser,
        ...decoded,
        role: isSuperAdmin ? 'SUPER_ADMIN' : isAdmin ? 'ADMIN' : roles[0],
        isAdmin,
        isSuperAdmin,
        groups,
        plan:
            decoded['custom:subscriptionTier'] ||
            (featureFlags.HOUSING_PRO ? 'housing-pro' : 'landlord-pro'),
    };
}

/**
 * Read-only, non-React check for a signed-in Super Admin. Staff accounts are
 * unrestricted — plan/tier/subscription gates never apply to them (evidence
 * safe, trial nags, premium-lock nav). Reads the flags buildUserFromToken
 * persists at login; honours the same multi-source spellings used across the
 * app (isSuperAdmin flag + SUPER_ADMIN role).
 */
export function isSuperAdminSession(): boolean {
    if (typeof localStorage === 'undefined') return false;
    return (
        localStorage.getItem('isSuperAdmin') === 'true' ||
        localStorage.getItem('userRole') === 'SUPER_ADMIN'
    );
}
